import * as React from 'react';
import pluralize from 'pluralize';
import Text from 'components/Text';

export function chunkDeviceClasses(
  deviceClasses: string[],
  splitAt: number = 2
): [string[], string] {
  if (!deviceClasses.length) {
    return [[], ''];
  }

  if (deviceClasses.length === 1) {
    return [deviceClasses, ''];
  }

  const splitIndex = deviceClasses.length > splitAt ? splitAt : deviceClasses.length - 1;
  const firsts = deviceClasses.slice(0, splitIndex);
  const remaining = deviceClasses.length - splitAt;
  const last = remaining > 1 ? `${remaining} others` : deviceClasses[deviceClasses.length - 1];
  return [firsts, last];
}

type Props = {
  deviceName: string;
  deviceClasses: string[];
};

export default function ClaimDeviceClasses(props: Props) {
  const hasClasses = () => props.deviceClasses.length > 0;

  const renderDeviceText = () =>
    `${props.deviceName} is associated with an existing Remind account`;

  const renderClassesText = () => {
    const [firsts, last] = chunkDeviceClasses(props.deviceClasses);

    if (!firsts.length) {
      return '';
    }

    return (
      <Text>
        {firsts
          .map<React.ReactNode>(name => <Text weight="semibold">{name}</Text>)
          .reduce((prev, curr) => [prev, ', ', curr])}
        {last ? ' and ' : ''}
        <Text weight="semibold">{last}</Text>.
      </Text>
    );
  };

  const renderWithClasses = () => {
    return (
      <>
        {renderDeviceText()} with the {pluralize('class', props.deviceClasses.length)}{' '}
        {renderClassesText()}
      </>
    );
  };

  return (
    <Text element="p" size="small" weight="normal" flush>
      {hasClasses() ? renderWithClasses() : `${renderDeviceText()}.`}
    </Text>
  );
}
